import { reportWidgetEvent } from '../analytics/report-widget-event.js';
import {
  getHumanGateAgeMs,
  isHumanReady,
  markHumanGateInstalled,
  markHumanReady,
  setHumanGateEnabled,
} from './human-gate-state.js';
import type { HumanReadyReason, InstallHumanGateOptions } from './types.js';

const POINTER_MOVE_THRESHOLD_PX = 8;

let stopListeners: (() => void) | null = null;
let telemetryInstalled = false;

function emitHumanReady(reason: HumanReadyReason): void {
  reportWidgetEvent('human_ready', {
    reason,
    time_to_ready_ms: getHumanGateAgeMs(),
  });
}

function emitSessionEndedWithoutHuman(): void {
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '/';
  reportWidgetEvent('session_ended_without_human', {
    pathname,
    time_on_page_ms: getHumanGateAgeMs(),
  });
}

function openGate(reason: HumanReadyReason): void {
  if (!markHumanReady(reason)) return;
  emitHumanReady(reason);
  stopListeners?.();
  stopListeners = null;
}

function attachInteractionListeners(): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  let lastPointerX: number | null = null;
  let lastPointerY: number | null = null;

  const onScroll = () => openGate('scroll');
  const onTouch = () => openGate('touch');
  const onKey = () => openGate('keydown');
  const onPointerDown = (event: PointerEvent) => {
    lastPointerX = event.clientX;
    lastPointerY = event.clientY;
  };
  const onPointerMove = (event: PointerEvent) => {
    if (lastPointerX == null || lastPointerY == null) {
      lastPointerX = event.clientX;
      lastPointerY = event.clientY;
      return;
    }
    const dx = event.clientX - lastPointerX;
    const dy = event.clientY - lastPointerY;
    if (
      dx * dx + dy * dy >=
      POINTER_MOVE_THRESHOLD_PX * POINTER_MOVE_THRESHOLD_PX
    ) {
      openGate('pointer');
    }
  };

  const scrollOpts: AddEventListenerOptions = { passive: true, capture: true };
  window.addEventListener('scroll', onScroll, scrollOpts);
  window.addEventListener('touchstart', onTouch, scrollOpts);
  window.addEventListener('keydown', onKey, true);
  window.addEventListener('pointerdown', onPointerDown, true);
  window.addEventListener('pointermove', onPointerMove, scrollOpts);

  return () => {
    window.removeEventListener('scroll', onScroll, scrollOpts);
    window.removeEventListener('touchstart', onTouch, scrollOpts);
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('pointerdown', onPointerDown, true);
    window.removeEventListener('pointermove', onPointerMove, scrollOpts);
  };
}

function installPageHideTelemetry(): () => void {
  if (typeof window === 'undefined') return () => {};
  if (telemetryInstalled) return () => {};
  telemetryInstalled = true;

  const onPageHide = () => {
    if (!isHumanReady()) {
      emitSessionEndedWithoutHuman();
    }
  };

  window.addEventListener('pagehide', onPageHide);
  return () => {
    window.removeEventListener('pagehide', onPageHide);
    telemetryInstalled = false;
  };
}

/**
 * Start listening for human engagement signals.
 * Idempotent for listeners; call once from bootstrap.
 */
export function installHumanGate(
  options: InstallHumanGateOptions = {},
): () => void {
  const enabled = options.enabled !== false;
  setHumanGateEnabled(enabled);
  markHumanGateInstalled();

  const stopTelemetry = installPageHideTelemetry();

  if (!enabled) {
    openGate('disabled');
    return () => {
      stopTelemetry();
      stopListeners?.();
      stopListeners = null;
    };
  }

  if (isHumanReady()) {
    return () => {
      stopTelemetry();
    };
  }

  stopListeners?.();
  stopListeners = attachInteractionListeners();

  return () => {
    stopTelemetry();
    stopListeners?.();
    stopListeners = null;
  };
}

/**
 * Open the gate from launcher / chat open.
 * Safe to call before or after `installHumanGate`.
 */
export function markHumanReadyFromUi(reason: HumanReadyReason = 'launcher'): void {
  markHumanGateInstalled();
  openGate(reason);
}
